// src/components/VitalsIntakeForm.jsx
//
// Front-desk vitals + intake dialog, opened from a Waiting card on the clinic
// queue (and reusable from a patient page). Two calls, in order:
//   1. POST /clinic/patients/{id}/vitals   (only when a vital was entered)
//   2. PUT  /clinic/visits/by-appointment/{appointmentId}/intake
//      (chief complaint + current medicines/allergies note — visit workstream)
// If (2) fails the vitals are already saved, so the form closes with a warning
// toast instead of asking the nurse to re-enter and double-record them.
//
// Props: { patientId, patientName, appointmentId?, onClose(), onSaved?() }
// Without an appointmentId (the doctor's patient record) only vitals are asked for: the
// chief complaint / medicines note is keyed by appointment.
// Doctors record from the consultation screen and the patient's Vitals tab too.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '../context/ToastContext';
import useDialogFocus from '../hooks/useDialogFocus';
import { vitalsAPI, saveIntake } from '../services/vitalsApi';
import {
  VITAL_FIELDS, EMPTY_VITALS, validateVitals, hasAnyVital, computeBmi, bpHint,
  toVitalsPayload, toIntakePayload,
} from '../utils/vitalsForm';

const FIELD_BY_KEY = Object.fromEntries(VITAL_FIELDS.map((f) => [f.key, f]));
// Display order and layout; ranges and units come from utils/vitalsForm.
const ROWS = [['systolic', 'diastolic'], ['pulse', 'spo2'], ['temperatureC'], ['weightKg', 'heightCm']];

function errMessage(e, fallback) {
  const d = e?.response?.data;
  return (typeof d === 'string' && d) || d?.message || d?.error || fallback;
}

function formatPrevious(r) {
  const parts = [];
  if (r.systolic != null && r.diastolic != null) parts.push(`BP ${r.systolic}/${r.diastolic}`);
  if (r.pulse != null) parts.push(`pulse ${r.pulse}`);
  if (r.spo2 != null) parts.push(`SpO₂ ${r.spo2}%`);
  if (r.temperatureC != null) parts.push(`${r.temperatureC} °C`);
  if (r.weightKg != null) parts.push(`${r.weightKg} kg`);
  if (r.heightCm != null) parts.push(`${r.heightCm} cm`);
  if (r.bmi != null) parts.push(`BMI ${r.bmi}`);
  return parts.join(' · ');
}

export default function VitalsIntakeForm({ patientId, patientName, appointmentId, onClose, onSaved }) {
  const { toast } = useToast();
  const [values, setValues] = useState(EMPTY_VITALS);
  const [chiefComplaint, setChiefComplaint] = useState('');
  const [currentMedicines, setCurrentMedicines] = useState('');
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [previous, setPrevious] = useState({ state: 'loading', reading: null });
  const firstRef = useRef(null);
  const dialogRef = useRef(null);
  useDialogFocus(dialogRef);

  useEffect(() => { firstRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  useEffect(() => {
    let stale = false;
    vitalsAPI.list(patientId, 1)
      .then((r) => {
        if (stale) return;
        const list = Array.isArray(r.data) ? r.data : [];
        setPrevious({ state: 'ready', reading: list[0] || null });
      })
      .catch(() => { if (!stale) setPrevious({ state: 'error', reading: null }); });
    return () => { stale = true; };
  }, [patientId]);

  const errors = useMemo(() => validateVitals(values), [values]);
  const bmi = computeBmi(values.weightKg, values.heightCm);
  const bp = bpHint(values.systolic, values.diastolic);
  const showError = (k) => (submitted || touched[k]) && errors[k];

  const setField = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.value }));
  const blur = (k) => () => setTouched((t) => ({ ...t, [k]: true }));

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitted(true);
    setFormError('');
    const intake = toIntakePayload({ chiefComplaint, currentMedicines });
    if (Object.keys(errors).length) return;
    if (!hasAnyVital(values) && !intake) {
      setFormError('Enter at least one vital sign or a chief complaint.');
      return;
    }
    setSaving(true);
    try {
      if (hasAnyVital(values)) {
        await vitalsAPI.record(patientId, toVitalsPayload(values, { appointmentId }));
      }
    } catch (err) {
      setFormError(errMessage(err, 'Could not save vitals. Please try again.'));
      setSaving(false);
      return;
    }
    let intakeFailed = false;
    let intakeError = null;
    if (intake) {
      if (!appointmentId) intakeFailed = true; // intake is keyed by appointment
      else { try { await saveIntake(appointmentId, intake); } catch (err) { intakeFailed = true; intakeError = err; } }
    }
    setSaving(false);
    if (intakeFailed && !hasAnyVital(values)) {
      // Nothing was saved (no vitals were entered), so don't claim "vitals saved":
      // keep the form open so the note is not lost.
      setFormError(errMessage(intakeError, 'Could not save the chief complaint / medicines note. Please try again.'));
      return;
    }
    if (intakeFailed) {
      toast.warning('Vitals saved, but the chief complaint / medicines note could not be saved. Add it again from the visit.');
    } else {
      toast.success(`Vitals recorded${patientName ? ` for ${patientName}` : ''}`);
    }
    if (onSaved) onSaved();
    onClose();
  }

  const field = (key) => {
    const f = FIELD_BY_KEY[key];
    const id = `vitals-${key}`;
    const err = showError(key);
    return (
      <div className="form-group" key={key}>
        <label className="form-label" htmlFor={id}>{f.label} ({f.unit})</label>
        <input
          id={id}
          ref={key === 'systolic' ? firstRef : undefined}
          className="form-input"
          type="number"
          inputMode="decimal"
          step="any"
          min={f.min}
          max={f.max}
          value={values[key]}
          onChange={setField(key)}
          onBlur={blur(key)}
          aria-invalid={err ? 'true' : undefined}
          aria-describedby={err ? `${id}-err` : `${id}-hint`}
        />
        {err
          ? <div id={`${id}-err`} className="form-error" role="alert">{errors[key]}</div>
          : <div id={`${id}-hint`} className="form-hint">{f.min}–{f.max}</div>}
      </div>
    );
  };

  return (
    <div className="dialog-backdrop" onClick={saving ? undefined : onClose}>
      <form
        className="dialog dialog--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="vitals-title"
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        noValidate
      >
        <div className="dialog-title" id="vitals-title">Record vitals</div>
        <div className="dialog-sub">{patientName || 'Patient'} — leave a field blank if it was not measured.</div>

        <div className="vitals-prev" aria-live="polite">
          {previous.state === 'loading' && 'Loading previous reading…'}
          {previous.state === 'error' && 'Previous reading unavailable.'}
          {previous.state === 'ready' && !previous.reading && 'No previous reading on file.'}
          {previous.state === 'ready' && previous.reading && (
            <>
              <strong>Previous reading</strong>
              {previous.reading.measuredAt ? ` (${new Date(previous.reading.measuredAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })})` : ''}
              {': '}{formatPrevious(previous.reading) || 'no values'}
            </>
          )}
        </div>

        {ROWS.map((row) => (
          <div className="form-row" key={row.join('-')}>{row.map(field)}</div>
        ))}

        {bp === 'high' && (
          <div className="alert alert-warning" role="status">
            Blood pressure is in the high range (140/90 or above). Informational only — please tell the doctor.
          </div>
        )}
        {bp === 'low' && (
          <div className="alert alert-warning" role="status">
            Blood pressure is in the low range (below 90/60). Informational only — please tell the doctor.
          </div>
        )}

        <div className="vitals-bmi" aria-live="polite">
          BMI: <strong>{bmi != null ? bmi : '—'}</strong>
          {bmi == null && <span className="form-hint"> (needs weight and height)</span>}
        </div>

        {appointmentId && (<>
        <div className="form-group">
          <label className="form-label" htmlFor="vitals-complaint">Chief complaint</label>
          <textarea id="vitals-complaint" className="form-textarea" rows={2} maxLength={500}
            value={chiefComplaint} onChange={(e) => setChiefComplaint(e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="vitals-meds">Current medicines and allergies</label>
          <textarea id="vitals-meds" className="form-textarea" rows={2} maxLength={1000}
            value={currentMedicines} onChange={(e) => setCurrentMedicines(e.target.value)} />
        </div>
        </>)}

        {formError && <div className="alert alert-error" role="alert">{formError}</div>}

        <div className="dialog-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save vitals'}
          </button>
        </div>
      </form>
    </div>
  );
}
